import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useTranslation } from '@cravo/i18n'

/**
 * Full-screen blocker shown when backend `/platform-config/version` reports
 * `maintenanceMode: true` OR the current web build is below `minWebVersion`.
 * Mobile parity: business_splash_controller.dart force-update / maintenance gate.
 */
export function MaintenanceGate({
  mode,
  message,
  updateUrl,
}: {
  mode: 'maintenance' | 'force_update'
  message?: string
  updateUrl?: string
}) {
  const { t } = useTranslation()
  const isUpdate = mode === 'force_update'
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--ink-50)',
        padding: 24,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 460,
          width: '100%',
          textAlign: 'center',
          padding: 32,
          background: 'white',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: 'var(--warning-bg)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {isUpdate ? (
            <RefreshCw size={28} style={{ color: 'var(--warning)' }} />
          ) : (
            <AlertTriangle size={28} style={{ color: 'var(--warning)' }} />
          )}
        </div>

        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink-950)' }}>
          {isUpdate ? t('components.maintenanceGate.updateTitle') : t('components.maintenanceGate.maintenanceTitle')}
        </h1>
        <p style={{ margin: '10px 0 22px', fontSize: 14, color: 'var(--ink-500)', lineHeight: 1.5 }}>
          {message ??
            (isUpdate
              ? t('components.maintenanceGate.updateBody')
              : t('components.maintenanceGate.maintenanceBody'))}
        </p>

        {isUpdate && updateUrl ? (
          <a
            href={updateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn primary"
            style={{ display: 'inline-flex', padding: '10px 18px' }}
          >
            {t('components.maintenanceGate.updateNow')}
          </a>
        ) : (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn primary"
            style={{ padding: '10px 18px' }}
          >
            {t('components.maintenanceGate.tryAgain')}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Compare two semver-ish strings (e.g. "1.2.3"). Returns -1, 0, 1.
 * Treats missing parts as 0. Non-numeric parts are ignored.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((p) => parseInt(p, 10) || 0)
  const pb = b.split('.').map((p) => parseInt(p, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const x = pa[i] ?? 0
    const y = pb[i] ?? 0
    if (x < y) return -1
    if (x > y) return 1
  }
  return 0
}
